import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getPackageTitle, getPackageName } from '../../utils/packages';
import { Money, Username } from '../../utils/privacyMask';
import './GlobalSearch.css';

export default function GlobalSearch({ onClose }) {
  const navigate  = useNavigate();
  const inputRef  = useRef(null);

  const [query, setQuery]   = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData]     = useState({ accounts: [], subscriptions: [], transactions: [] });

  /* ── Focus input + Escape handler ── */
  useEffect(() => {
    inputRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* ── Fetch all data once ── */
  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [accRes, subRes, txRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/account`,              { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/subscription?all=true`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/transaction`,           { headers }),
        ]);
        setData({ accounts: accRes.data, subscriptions: subRes.data, transactions: txRes.data });
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const nameMap = useMemo(() => {
    const m = {};
    data.accounts.forEach(a => { m[a._id] = a.username; });
    return m;
  }, [data.accounts]);

  /* ── Filter results ── */
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    return {
      accounts: data.accounts.filter(a =>
        a.username?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
      ).slice(0, 5),
      subscriptions: data.subscriptions.filter(s =>
        s.username?.toLowerCase().includes(q) ||
        nameMap[s.accountID]?.toLowerCase().includes(q) ||
        s.notes?.toLowerCase().includes(q) ||
        getPackageTitle(s.package)?.toLowerCase().includes(q)
      ).slice(0, 5),
      transactions: data.transactions.filter(t =>
        nameMap[t.accountID]?.toLowerCase().includes(q) ||
        t.notes?.toLowerCase().includes(q) ||
        t.paymentMethod?.toLowerCase().includes(q)
      ).slice(0, 4),
    };
  }, [query, data, nameMap]);

  const go = path => { navigate(path); onClose(); };

  const total = results ? results.accounts.length + results.subscriptions.length + results.transactions.length : 0;

  return (
    <div className="gs-overlay" onClick={onClose}>
      <div className="gs-box" onClick={e => e.stopPropagation()}>

        {/* ── Input ── */}
        <div className="gs-input-wrap">
          <i className="bi bi-search gs-search-icon" />
          <input
            ref={inputRef}
            className="gs-input"
            placeholder="Search accounts, subscriptions, transactions…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
          />
          {query
            ? <button className="gs-clear" onClick={() => setQuery('')}><i className="bi bi-x-lg" /></button>
            : <span className="gs-kbd">ESC</span>
          }
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="gs-state"><i className="bi bi-hourglass-split" />Loading…</div>
        )}

        {/* ── Prompt ── */}
        {!loading && !results && (
          <div className="gs-state"><i className="bi bi-search" />Type 2+ characters to search</div>
        )}

        {/* ── No results ── */}
        {!loading && results && total === 0 && (
          <div className="gs-state">
            <i className="bi bi-inbox" />
            No results for <strong style={{ color: '#cbd5e1', marginLeft: 4 }}>"{query}"</strong>
          </div>
        )}

        {/* ── Results ── */}
        {!loading && results && total > 0 && (
          <div className="gs-results">

            {results.accounts.length > 0 && (
              <>
                <div className="gs-group-label">Accounts</div>
                {results.accounts.map(a => (
                  <button key={a._id} className="gs-item" onClick={() => go(`/account/view/${a._id}`)}>
                    <span className="gs-item-icon" style={a.accountType === 'reseller'
                      ? { color: '#a78bfa', background: 'rgba(167,139,250,0.1)' }
                      : { color: '#f59e0b', background: 'rgba(245,158,11,0.1)' }}>
                      <i className={`bi ${a.accountType === 'reseller' ? 'bi-person-badge-fill' : 'bi-person-fill'}`} />
                    </span>
                    <div className="gs-item-body">
                      <span className="gs-item-title">
                        <Username value={a.username} />
                        {a.accountType === 'reseller' && (
                          <span style={{ marginLeft: 7, fontSize: '0.7rem', fontWeight: 600, color: '#c4b5fd' }}>RESELLER</span>
                        )}
                      </span>
                      {a.email && <span className="gs-item-sub">{a.email}</span>}
                    </div>
                    <i className="bi bi-arrow-right gs-item-arrow" />
                  </button>
                ))}
              </>
            )}

            {results.subscriptions.length > 0 && (
              <>
                <div className="gs-group-label">Subscriptions</div>
                {results.subscriptions.map(s => (
                  <button key={s._id} className="gs-item" onClick={() => go(`/subscription/view/${s._id}`)}>
                    <span className="gs-item-icon" style={{ color: '#3b82f6', background: 'rgba(59,130,246,0.1)' }}>
                      <i className="bi bi-bookmark-star-fill" />
                    </span>
                    <div className="gs-item-body">
                      <span className="gs-item-title"><Username value={nameMap[s.accountID] || s.username || 'Unknown'} /></span>
                      <span className="gs-item-sub">{getPackageTitle(s.package)} — {getPackageName(s.package)}</span>
                    </div>
                    <span style={{
                      fontSize: '0.65rem', padding: '2px 8px', borderRadius: 20, flexShrink: 0,
                      background: s.active ? 'rgba(34,197,94,0.1)' : 'rgba(248,113,113,0.1)',
                      color: s.active ? '#4ade80' : '#f87171',
                      border: `1px solid ${s.active ? 'rgba(34,197,94,0.2)' : 'rgba(248,113,113,0.2)'}`,
                    }}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                    <i className="bi bi-arrow-right gs-item-arrow" />
                  </button>
                ))}
              </>
            )}

            {results.transactions.length > 0 && (
              <>
                <div className="gs-group-label">Transactions</div>
                {results.transactions.map(t => (
                  <button key={t._id} className="gs-item" onClick={() => go(`/transaction/view/${t._id}`)}>
                    <span className="gs-item-icon" style={{ color: '#a78bfa', background: 'rgba(167,139,250,0.1)' }}>
                      <i className="bi bi-receipt" />
                    </span>
                    <div className="gs-item-body">
                      <span className="gs-item-title"><Username value={nameMap[t.accountID] || 'Unknown'} /></span>
                      <span className="gs-item-sub">
                        <Money value={t.revenue} /> · {t.paymentMethod || '—'}{t.notes ? ` · ${t.notes}` : ''}
                      </span>
                    </div>
                    <i className="bi bi-arrow-right gs-item-arrow" />
                  </button>
                ))}
              </>
            )}

          </div>
        )}

        {/* ── Footer ── */}
        <div className="gs-footer">
          <span>{total > 0 ? `${total} result${total !== 1 ? 's' : ''}` : ''}</span>
          <span>Click a result or press <strong style={{ color: '#475569' }}>ESC</strong> to close</span>
        </div>

      </div>
    </div>
  );
}
